import {
  Box,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState, useCallback, useRef } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";
import { CardField } from "src/utils/card/cardData";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { fetchDependentCardData } from "src/utils/card/fetchDependentCardData";

interface PrismScoreFormData {
  PS_ID: string;
  PROCESS_TIME: Date | null;
  NURSE_ID: string;
  CTIME: Date;
  CUSER: string;
  SYSTOLIC_BLOOD_PRESSURE: string;
  SYSTOLIC_BLOOD_PRESSURE_XVALUE: string;
  DIASTOLIC_BLOOD_PRESSURE: string;
  DIASTOLIC_BLOOD_PRESSURE_XVALUE: string;
  HEART_RATE: string;
  HEART_RATE_XVALUE: string;
  RESPIRATORY_RATE: string;
  RESPIRATORY_RATE_XVALUE: string;
  PAO2_FLO2: string;
  PAO2_FLO2_XVALUE: string;
  PACO2: string;
  PACO2_XVALUE: string;
  PT_PTT: string;
  PT_PTT_XVALUE: string;
  TOTAL_BILIRUBIN: string;
  TOTAL_BILIRUBIN_XVALUE: string;
  CALCIUM: string;
  CALCIUM_XVALUE: string;
  POTASSIUM: string;
  POTASSIUM_XVALUE: string;
  GLUCOSE: string;
  GLUCOSE_XVALUE: string;
  HCO3: string;
  HCO3_XVALUE: string;
  PUPIL_REACTION: string;
  PUPIL_REACTION_XVALUE: string;
  PRISM_SCORE: number;
  GLASGOW_SCORE: string;
  GLASGOW_SCORE_VALUE: string;
  LESS_THEN: string;
  EXPECTED_DEATH_RATE: number;
  POSTOPERATIVE_EXPECTED_DEATH: number;
  HOW_MANY_MONTHS: number;
  MICRDS_ID: string;
  NURSE_NOTE: string;
}

interface PrismScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof PrismScoreFormData, value: any) => void;
  patientData: any;
  showAlert?: (message: string, severity: "error" | "success") => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function PrismScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  patientId,
  getComId,
  comId,
  onClose,
  showAlert,
  isSaving,
  extraAccess,
}: PrismScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    cardData,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "PRISM_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof PrismScoreFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as PrismScoreFormData;
  const [glasgowScoreValue, setGlasgowScoreValue] = useState<number | null>(null);
  const [glasgowCheckComplete, setGlasgowCheckComplete] = useState<boolean>(!!recordId);
  const glasgowFetchedRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setGlasgowCheckComplete(!!recordId);
    glasgowFetchedRef.current = false;
  }, [recordId]);

  const applyPrismGlasgowValues = useCallback(
    (glasgowScore: number, glasgowScoreId?: number | null) => {
      setGlasgowScoreValue(glasgowScore);
      handleChange("GLASGOW_SCORE", glasgowScore.toString());
      parentHandleChange("GLASGOW_SCORE", glasgowScore.toString());
      if (glasgowScoreId) {
        handleChange("GLASGOW_SCORE_VALUE", glasgowScoreId);
        parentHandleChange("GLASGOW_SCORE_VALUE", glasgowScoreId);
      }
    },
    [handleChange, parentHandleChange]
  );

  useEffect(() => {
    const isEditMode = !!recordId;

    const fetchLatestGlasgowScore = async () => {
      if (glasgowFetchedRef.current) return;

      // Edit modunda formData yüklenene kadar bekle
      if (isEditMode && (!typedFormData.PS_ID || typedFormData.PS_ID === "")) {
        glasgowFetchedRef.current = false;
        return;
      }

      glasgowFetchedRef.current = true;

      // Edit modunda kaydın kendi glasgow skorunu kullan, güncel skor çekme
      if (isEditMode) {
        const existingScore = Number(typedFormData.GLASGOW_SCORE) || 0;
        if (existingScore > 0) {
          const glasgowScoreId = typedFormData.GLASGOW_SCORE_VALUE
            ? Number(typedFormData.GLASGOW_SCORE_VALUE) || null
            : null;
          applyPrismGlasgowValues(existingScore, glasgowScoreId);
        }
        setGlasgowCheckComplete(true);
        return;
      }

      // Create modunda en son Glasgow kaydını çek
      try {
        const dependency = await fetchDependentCardData({
          cardType: "GLASGOW_SCORE",
          patientId,
          orId: 2571,
        });

        const latestDetail = dependency?.detailRecord;
        if (!latestDetail) {
          if (isMountedRef.current && showAlert && onClose) {
            showAlert(t("prism-score.glasgow-score-required"), "error");
            onClose();
          }
          setGlasgowCheckComplete(true);
          return;
        }

        const seeScore = Number(latestDetail.SEE_SCORE) || 0;
        const verbalScore = Number(latestDetail.VERBAL_SCORE) || 0;
        const motorScore = Number(latestDetail.MOTOR_SCORE) || 0;
        const glasgowScore = seeScore + verbalScore + motorScore;
        const glasgowScoreId = latestDetail.ID || null;

        if (glasgowScore > 2) {
          applyPrismGlasgowValues(glasgowScore, glasgowScoreId);
        } else if (isMountedRef.current && showAlert && onClose) {
          showAlert(t("prism-score.glasgow-score-required"), "error");
          onClose();
        }
      } catch (error) {
        console.error("Error fetching Glasgow Score:", error);
        if (isMountedRef.current && showAlert && onClose) {
          showAlert("Glasgow skoru hesaplanmadan Prism Skoru hesaplanamaz!", "error");
          onClose();
        }
      } finally {
        setGlasgowCheckComplete(true);
      }
    };

    fetchLatestGlasgowScore();
  }, [
    applyPrismGlasgowValues,
    onClose,
    parentHandleChange,
    patientId,
    recordId,
    showAlert,
    t,
    typedFormData.PS_ID,
  ]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "PRISM_SCORE") {
        parentHandleChange(key as keyof PrismScoreFormData, value);
      }
    });
  }, [formData, parentHandleChange]);

  // İlk açılışta hasta yaşına göre LESS_THEN ve HOW_MANY_MONTHS'u ayarla
  useEffect(() => {
    if (recordId || !patientData) return;

    try {
      const patientInfo = patientData.data || patientData;
      const birthDate = patientInfo.birthdate || patientInfo.BIRTH_DATE || patientInfo.BIRTHDATE;

      if (!birthDate) return;

      const birth = new Date(birthDate);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - birth.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Gün farkı


      let months = (today.getFullYear() - birth.getFullYear()) * 12;
      months += today.getMonth() - birth.getMonth();

      if (today.getDate() >= birth.getDate()) {
        months++;
      }

      handleChange("HOW_MANY_MONTHS", months);
      parentHandleChange("HOW_MANY_MONTHS", months);

      if (diffDays > 360) {
        handleChange("LESS_THEN", "1");
        parentHandleChange("LESS_THEN", "1");
      }
    } catch (error) {
      console.error("Error calculating patient age:", error);
    }
  }, [recordId, patientData, handleChange, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;

    const rowData: any = {
      SYSTOLIC_BLOOD_PRESSURE_XVALUE: getScoreValue("SYSTOLIC_BLOOD_PRESSURE"),
      DIASTOLIC_BLOOD_PRESSURE_XVALUE: getScoreValue("DIASTOLIC_BLOOD_PRESSURE"),
      HEART_RATE_XVALUE: getScoreValue("HEART_RATE"),
      RESPIRATORY_RATE_XVALUE: getScoreValue("RESPIRATORY_RATE"),
      PAO2_FLO2_XVALUE: getScoreValue("PAO2_FLO2"),
      PACO2_XVALUE: getScoreValue("PACO2"),
      PT_PTT_XVALUE: getScoreValue("PT_PTT"),
      TOTAL_BILIRUBIN_XVALUE: getScoreValue("TOTAL_BILIRUBIN"),
      CALCIUM_XVALUE: getScoreValue("CALCIUM"),
      POTASSIUM_XVALUE: getScoreValue("POTASSIUM"),
      GLUCOSE_XVALUE: getScoreValue("GLUCOSE"),
      HCO3_XVALUE: getScoreValue("HCO3"),
      PUPIL_REACTION_XVALUE: getScoreValue("PUPIL_REACTION"),
    };

    const prismScoreField = cardData.fields.find(
      (f: any) => f.field === "prism_score"
    ) as CardField | undefined;
    if (prismScoreField?.calculate) {
      const calculatedScore = prismScoreField.calculate(rowData);
      setScore(calculatedScore);
      parentHandleChange("PRISM_SCORE", calculatedScore);
    }
  }, [
    cardData,
    getScoreValue,
    parentHandleChange,
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate]);


  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number,
    readOnly: boolean = false
  ) => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6}>
        <ByzMcParameterDetailSelect
          title={t("select")}
          mcpId={mcpId}
          multiple={false}
          handleChange={(val) => {
            if (!Array.isArray(val)) {
              handleMCPChange(fieldName, val);
            }
          }}
          value={selectedMCPs[fieldName]}
          sx={{ width: "100%" }}
          cachedData={getMCPParameterDetails(mcpId)}
          cacheLoaded={mcpCacheLoaded}
          readOnly={readOnly}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.PS_ID)}
              disabled={!glasgowCheckComplete || !extraAccess}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("prism-score.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ width: "100%" }}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                  label={t("datetime")}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("prism-score.patient-related-factors")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField("SYSTOLIC_BLOOD_PRESSURE", "prism-score.systolic-blood-pressure", 430, !extraAccess)}
            {renderMCPField("DIASTOLIC_BLOOD_PRESSURE", "prism-score.diastolic-blood-pressure", 431, !extraAccess)}
            {renderMCPField("HEART_RATE", "prism-score.heart-rate", 432, !extraAccess)}
            {renderMCPField("RESPIRATORY_RATE", "prism-score.respiratory-rate", 433, !extraAccess)}
            {renderMCPField("PAO2_FLO2", "prism-score.paofio", 434, !extraAccess)}
            {renderMCPField("PACO2", "prism-score.partial-carbon-dioxide-pressure", 435, !extraAccess)}
            {renderMCPField("PT_PTT", "prism-score.ptptt", 436, !extraAccess)}
            {renderMCPField("TOTAL_BILIRUBIN", "prism-score.total-bilirubin", 437, !extraAccess)}
            {renderMCPField("CALCIUM", "prism-score.calcium", 438, !extraAccess)}
            {renderMCPField("POTASSIUM", "prism-score.potassium", 439, !extraAccess)}
            {renderMCPField("GLUCOSE", "prism-score.glucose", 440, !extraAccess)}
            {renderMCPField("HCO3", "prism-score.bicarbonate", 441, !extraAccess)}
            {renderMCPField("PUPIL_REACTION", "prism-score.pupil-reaction", 442, !extraAccess)}

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  {t("prism-score.glasgow-score")}
                  <Box sx={{ display: "flex", alignItems: "center", width: "150px" }}>
                    <span style={{ fontSize: "30px", fontWeight: "100", marginRight: "8px" }}>|</span>
                    {t("prism-score.score")}
                  </Box>
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                  <ByzMuiTextField
                    sx={{ flex: 1 }}
                    label={t("prism-score.score-value")}
                    variant="outlined"
                    value={glasgowScoreValue !== null ? glasgowScoreValue.toString() : ""}
                    disabled
                  />
                  <ByzMuiTextField
                    sx={{ flex: 1 }}
                    label={t("prism-score.score")}
                    variant="outlined"
                    value={
                      glasgowScoreValue !== null
                        ? glasgowScoreValue >= 8
                          ? ">=8"
                          : "<8"
                        : ""
                    }
                    disabled
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("prism-score.greater-than-360-days")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiCheckBox
                  onChange={(e) =>
                    handleChange("LESS_THEN", e.target.checked ? "1" : "0")
                  }
                  checked={typedFormData.LESS_THEN === "1"}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("prism-score.month-count")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("prism-score.month-count")}
                  placeholder=""
                  type="number"
                  value={typedFormData.HOW_MANY_MONTHS}
                  onChange={(e) => handleChange("HOW_MANY_MONTHS", e.target.value ? Number(e.target.value) : 0)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("prism-score.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("prism-score.two-score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("prism-score.two-score")}
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("prism-score.note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("prism-score.note")}
                  placeholder=""
                  value={typedFormData.NURSE_NOTE}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("prism-score.expected-mortality-rate")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("prism-score.mortality-rate")}
                  value={typedFormData.EXPECTED_DEATH_RATE || ""}
                  placeholder=""
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("prism-score.postoperative-expected-mortality-rate")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  placeholder=""
                  label={t("prism-score.postoperative-mortality-rate")}
                  value={typedFormData.POSTOPERATIVE_EXPECTED_DEATH || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default PrismScoreCreate;
